/**
 * WaitingPage - Waiting for partner signature
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const USE_V2_API = process.env.REACT_APP_USE_V2_API === 'true';
const API_V2 = `${BACKEND_URL}/api/v2`;

const WaitingPage = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const MAX_POLLS = 60; // 5 minutes at 5-second intervals

  useEffect(() => {
    // Start polling for partner signature
    const relationshipId = sessionStorage.getItem('relationship_id');
    
    const interval = setInterval(async () => {
      if (pollCount >= MAX_POLLS) {
        clearInterval(interval);
        return;
      }

      try {
        setChecking(true);
        // Use V2 API for state checking
        const url = USE_V2_API 
          ? `${API_V2}/relationships/${relationshipId}`
          : `${API}/relationships/${relationshipId}`;
        const response = await axios.get(url);
        const state = response.data.state;

        console.log('[WAITING] Poll check - status:', state.status, 'both_signed:', state.both_signed, 'reveal_ready:', state.reveal_ready);

        // Check if reveal is ready (both signed)
        const revealStatuses = ['SIGNED_BOTH', 'COMPARISON_READY', 'CERT_READY', 'RESULTS_SENT'];
        if (revealStatuses.includes(state.status) || state.both_signed || state.reveal_ready) {
          clearInterval(interval);
          toast.success('🎉 Your partner has signed! Your Reveal is ready!');
          navigate(`/reveal/${relationshipId}`);
        }
        
        setPollCount(prev => prev + 1);
        setChecking(false);
      } catch (error) {
        console.error('[WAITING] Poll error:', error);
        setChecking(false);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [navigate, pollCount]);

  const handleRefresh = async () => {
    setChecking(true);
    try {
      const relationshipId = sessionStorage.getItem('relationship_id');
      // Use V2 API for state checking
      const url = USE_V2_API 
        ? `${API_V2}/relationships/${relationshipId}`
        : `${API}/relationships/${relationshipId}`;
      const response = await axios.get(url);
      const state = response.data.state;

      const revealStatuses = ['SIGNED_BOTH', 'COMPARISON_READY', 'CERT_READY', 'RESULTS_SENT'];
      if (revealStatuses.includes(state.status) || state.both_signed || state.reveal_ready) {
        toast.success('🎉 Your partner has signed! Your Reveal is ready!');
        navigate(`/reveal/${relationshipId}`);
      } else {
        toast.info('Still waiting for your partner to sign...');
      }
    } catch (error) {
      toast.error('Failed to check status');
    }
    setChecking(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 100px)'}}>
        <div className="max-w-lg w-full bg-white rounded-2xl border border-stone-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
          
          <h2 className="text-3xl font-bold text-stone-900 mb-4" data-testid="waiting-heading">
            Waiting for Your Partner
          </h2>
          
          <p className="text-stone-600 leading-relaxed mb-8">
            You've signed your MarrsingClause®! We're now waiting for your partner to complete their signature. 
            Once they sign, you'll both receive an email with your Reveal link.
          </p>

          <div className="bg-stone-50 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              {checking ? (
                <Loader2 className="w-5 h-5 animate-spin text-stone-600" />
              ) : (
                <Clock className="w-5 h-5 text-stone-600" />
              )}
              <span className="text-sm text-stone-600">
                {checking ? 'Checking...' : 'Auto-checking every 5 seconds'}
              </span>
            </div>
            <p className="text-sm text-stone-500">
              This page will automatically redirect when your partner signs.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <button
              onClick={handleRefresh}
              disabled={checking}
              className="w-full bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="check-status"
            >
              {checking ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  Check Now
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="text-stone-600 hover:text-stone-900 text-sm"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitingPage;
